import { lazy, Suspense, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, MapPin, Vote, Radio, Users } from "lucide-react";
import { MaskedLines, FadeUp, SectionHeading, Counter } from "@/components/Reveal";
import SignalRings from "@/components/SignalRings";
import Marquee from "@/components/Marquee";
import TiltCard from "@/components/TiltCard";
import FounderSection from "@/components/FounderSection";
import WinningGraph from "@/components/WinningGraph";
import MPExplorer from "@/components/MPExplorer";
import Testimonials from "@/components/Testimonials";
import ElectionCountdown from "@/components/ElectionCountdown";
import Magnetic from "@/components/Magnetic";
import { SECTORS } from "@/data/content";
import ServiceRows from "@/components/ServiceRows";
import RotatingWord from "@/components/RotatingWord";
import { PROJECTS } from "@/data/projects";
import useSeo from "@/hooks/useSeo";

const Hero3D = lazy(() => import("@/components/Hero3D"));

const HERO_IMG =
  "https://images.unsplash.com/photo-1716047269726-56a1781be730?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxJbmRpYSUyMHBvbGl0aWNhbCUyMHJhbGx5JTIwY3Jvd2QlMjBmbGFnc3xlbnwwfHx8fDE3OTEwNTQxNzF8MA&ixlib=rb-4.1.0&q=85";

const INDORE_IMG =
  "https://images.unsplash.com/photo-1679556369532-bacc9cbdb8da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxJbmRvcmUlMjBSYWp3YWRhJTIwcGFsYWNlfGVufDB8fHx8MTc5MTA5MzIyMHww&ixlib=rb-4.1.0&q=85";

const BENTO_SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];

const EDGE = [
  { icon: Vote, title: "Booth-level thinking", desc: "Every plan starts with the voter list, not the hoarding." },
  { icon: Radio, title: "Hindi-first war room", desc: "Creators who think in Hindi, Malwi and Nimadi — and answer an attack in under an hour." },
  { icon: Users, title: "One team, zero hand-offs", desc: "Strategists, filmmakers, data analysts and ground teams under one Indore roof." },
  { icon: MapPin, title: "Indore roots, Bharat reach", desc: "Headquartered in India's cleanest city. 50+ districts, six states, one standard." },
];

export default function Home() {
  useSeo();
  const heroRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.3, 0.05]);
  const sceneY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const onMouseMove = (e) => {
    mouse.current = { x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 };
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={onMouseMove}
        className="relative flex min-h-svh flex-col justify-end overflow-hidden"
        data-testid="hero-section"
      >
        <motion.div className="absolute inset-0" style={{ y: bgY, opacity: bgOpacity }}>
          <img src={HERO_IMG} alt="" className="h-[120%] w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="signal-glow absolute inset-0" />
        <div className="saffron-glow absolute inset-0" />

        <motion.div
          className="pointer-events-none relative mt-24 h-[44vh] w-full lg:absolute lg:inset-y-0 lg:left-auto lg:right-0 lg:mt-0 lg:h-full lg:w-[58%]"
          style={{ y: sceneY }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Suspense fallback={null}>
            <Hero3D mouse={mouse} scroll={scrollYProgress} className="h-full w-full" />
          </Suspense>
        </motion.div>

        <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-end gap-12 px-6 pb-16 pt-4 lg:grid-cols-12 lg:px-12 lg:pt-36">
          <div className="lg:col-span-8">
            <motion.p
              className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7 }}
            >
              <span className="tricolor-line inline-block h-[3px] w-10 rounded-full" />
              Political & Creative Agency — Indore, Madhya Pradesh
            </motion.p>
            <h1 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
              <MaskedLines
                delay={0.25}
                inView={false}
                lines={["Strategy that", "wins the"]}
              />
              <RotatingWord />
            </h1>
            <motion.p
              className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
            >
              Election war rooms, booth-level ground game, chunavi geet and brand films — built in Indore,
              run across India. We build the narrative. The voter delivers the mandate.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <Magnetic>
                <Link
                  to="/work"
                  data-testid="hero-cta-work"
                  className="group inline-flex items-center gap-2 rounded-full btn-ribbon px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 "
                >
                  See our campaigns
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Magnetic>
              <Magnetic>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.15em] text-paper transition-colors duration-300 hover:border-saffron hover:text-saffron"
                >
                  Plan a campaign
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </motion.div>

            <motion.div
              className="mt-16 grid max-w-xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
            >
              {[
                { to: 40, suffix: "+", label: "Campaigns run" },
                { to: 50, suffix: "+", label: "Districts covered" },
                { to: 12, suffix: "Cr+", label: "Voter impressions" },
              ].map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Counter to={s.to} suffix={s.suffix} className="font-display text-3xl font-bold text-paper sm:text-4xl" />
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* EDGE */}
      <section className="mx-auto max-w-[1440px] px-6 pt-24 lg:px-12 lg:pt-32" data-testid="home-edge-section">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {EDGE.map((e, i) => (
            <FadeUp key={e.title} delay={i * 0.08}>
              <TiltCard testId={`edge-card-${i}`}>
                <div className="group h-full rounded-xl border border-white/10 bg-coal/60 p-7 transition-colors duration-500 hover:border-saffron/40">
                  <e.icon className="h-6 w-6 text-saffron" />
                  <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-tight text-paper">{e.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{e.desc}</p>
                </div>
              </TiltCard>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SELECTED WORK */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32" data-testid="home-work-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading overline="Selected Campaigns" lines={["Results,", "not just reels."]} testId="home-work-heading" />
          <FadeUp delay={0.2}>
            <Link
              to="/work"
              data-testid="home-work-viewall"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-brand"
            >
              View all campaigns
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {PROJECTS.slice(0, 4).map((p, i) => (
            <FadeUp key={p.slug} className={`col-span-1 ${BENTO_SPANS[i]}`} delay={i * 0.08}>
              <Link
                to="/work"
                data-testid={`home-work-card-${p.slug}`}
                className="group relative block h-[380px] overflow-hidden rounded-xl border border-white/10 transition-colors duration-500 hover:border-brand/40 lg:h-[440px]"
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6 lg:p-8">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron">{p.category}</p>
                    <h3 className="mt-2 font-display text-2xl font-bold uppercase tracking-tight text-paper lg:text-3xl">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">{p.client}</p>
                  </div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 text-paper opacity-0 transition-all duration-500 group-hover:border-brand group-hover:bg-brand group-hover:opacity-100">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <WinningGraph />

      {/* SERVICES PREVIEW */}
      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12 lg:pb-32" data-testid="home-services-section">
        <SectionHeading overline="What we do" lines={["Six disciplines.", "One war room."]} testId="home-services-heading" />
        <div className="mt-14">
          <ServiceRows />
        </div>
        <FadeUp delay={0.1}>
          <Link
            to="/services"
            data-testid="home-services-viewall"
            className="group mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-brand"
          >
            All six disciplines
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </section>

      <MPExplorer />

      <Testimonials />

      <FounderSection />

      <ElectionCountdown />

      {/* ABOUT TEASER */}
      <section className="border-t border-white/10 bg-coal/40" data-testid="home-about-section">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-32">
          <div className="lg:col-span-5">
            <SectionHeading overline="The Agency" lines={["Born in Indore.", "Built for Bharat."]} testId="home-about-heading" />
            <FadeUp delay={0.15}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
                ONWARD MEDIA is an Indore-based team of political strategists, data analysts, filmmakers and
                ground organisers. From Vidhan Sabha seats across Malwa-Nimar to Lok Sabha war rooms and
                statewide government outreach — we have done the 2 a.m. shifts that win mandates. We bring
                the same discipline to brands that want to grow in Bharat.
              </p>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:text-brand"
              >
                Our story
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <div className="relative overflow-hidden rounded-xl border border-white/10">
                <img src={INDORE_IMG} alt="Gandhi Hall, Indore" className="aspect-[16/10] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-5 left-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                  Headquarters — Indore, Madhya Pradesh
                </p>
              </div>
            </FadeUp>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                { to: 6, suffix: "", label: "States worked in" },
                { to: 180, suffix: "+", label: "Films & jingles" },
                { to: 7, suffix: "", label: "Years onward" },
              ].map((s, i) => (
                <FadeUp key={s.label} delay={i * 0.08}>
                  <div data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Counter to={s.to} suffix={s.suffix} className="font-display text-3xl font-bold text-saffron sm:text-4xl" />
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee items={SECTORS} reverse outline testId="sectors-marquee" />

      {/* CTA BANNER */}
      <section className="relative overflow-hidden border-t border-white/10" data-testid="home-cta-section">
        <SignalRings className="pointer-events-none absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 opacity-20" />
        <div className="relative mx-auto max-w-[1440px] px-6 py-28 text-center lg:px-12 lg:py-40">
          <p className="font-hindi text-2xl text-saffron sm:text-3xl">आगे बढ़ो। हमेशा।</p>
          <h2 className="mt-6 font-display text-3xl font-bold uppercase leading-[1.02] tracking-tight text-paper sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Your next election", <span key="n" className="font-script normal-case italic text-brand">starts today.</span>]} />
          </h2>
          <FadeUp delay={0.25}>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="group mt-12 inline-flex items-center gap-2 rounded-full btn-ribbon px-9 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 "
            >
              Plan a campaign
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
